// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Box, Button, Group, Image, SegmentedControl, Stack, Text } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Attachment, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconCheck, IconEraser, IconUpload } from '@tabler/icons-react';
import { type JSX, useEffect, useRef, useState } from 'react';
import { FormCard } from '../../../components/FormCard';
import { clearProviderSignature, getProviderSignature, setProviderSignature } from '../../../utils/provider-signature';

interface ProviderSignatureSectionProps {
  practitioner: Practitioner;
  onSaved: (updated: Practitioner) => void;
}

const CANVAS_WIDTH = 560;
const CANVAS_HEIGHT = 180;

export function ProviderSignatureSection({ practitioner, onSaved }: ProviderSignatureSectionProps): JSX.Element {
  const medplum = useMedplum();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<'upload' | 'draw'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [saving, setSaving] = useState(false);
  const signature = getProviderSignature(practitioner);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.fillStyle = '#fff';
    context.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    context.strokeStyle = '#1f2937';
    context.lineWidth = 2;
    context.lineCap = 'round';
  }, []);

  const clearCanvas = (): void => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
  };

  const getCanvasPoint = (event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = event.currentTarget;
    const bounds = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - bounds.left) / bounds.width) * canvas.width,
      y: ((event.clientY - bounds.top) / bounds.height) * canvas.height,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const context = event.currentTarget.getContext('2d');
    if (!context) {
      return;
    }
    const point = getCanvasPoint(event);
    context.beginPath();
    context.moveTo(point.x, point.y);
    setDrawing(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawing) {
      return;
    }
    const context = event.currentTarget.getContext('2d');
    if (!context) {
      return;
    }
    const point = getCanvasPoint(event);
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      let attachment: Attachment;
      if (mode === 'upload') {
        if (!file) {
          throw new Error('Choose an image to upload.');
        }
        const binary = await medplum.createBinary({ data: file, contentType: file.type, filename: file.name });
        attachment = { url: binary.url, contentType: file.type, title: file.name };
      } else {
        const canvas = canvasRef.current;
        if (!canvas) {
          throw new Error('Signature canvas is unavailable.');
        }
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) {
          throw new Error('Could not read drawn signature.');
        }
        const binary = await medplum.createBinary({
          data: blob,
          contentType: 'image/png',
          filename: 'provider-signature.png',
        });
        attachment = { url: binary.url, contentType: 'image/png', title: 'Provider signature' };
      }
      const updated = await medplum.updateResource(setProviderSignature(practitioner, attachment));
      onSaved(updated);
      setFile(null);
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Signature saved' });
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (): Promise<void> => {
    setSaving(true);
    try {
      const updated = await medplum.updateResource(clearProviderSignature(practitioner));
      onSaved(updated);
      showNotification({ color: 'green', icon: <IconCheck size={16} />, message: 'Signature removed' });
    } catch (error) {
      showNotification({ color: 'red', message: normalizeErrorString(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormCard title="Signature">
      <Stack gap="sm">
        {signature?.url && (
          <Group align="flex-end" gap="md">
            <Image src={signature.url} alt="Saved provider signature" maw={360} mah={120} fit="contain" />
            <Button variant="subtle" color="red" onClick={handleRemove} loading={saving}>
              Remove signature
            </Button>
          </Group>
        )}
        {!signature && (
          <Text c="dimmed" size="sm">
            No signature saved. Upload an image or draw one below.
          </Text>
        )}
        <SegmentedControl
          value={mode}
          onChange={(value) => setMode(value as 'upload' | 'draw')}
          data={[
            { label: 'Upload image', value: 'upload' },
            { label: 'Draw signature', value: 'draw' },
          ]}
        />
        {mode === 'upload' ? (
          <Dropzone
            accept={['image/jpeg', 'image/png']}
            maxSize={5 * 1024 ** 2}
            onDrop={(files) => setFile(files[0] ?? null)}
          >
            <Group justify="center" mih={100} gap="xs">
              <IconUpload size={24} />
              <Text size="sm">{file?.name ?? 'Drop a JPG or PNG here, or click to choose'}</Text>
            </Group>
          </Dropzone>
        ) : (
          <Box>
            <canvas
              ref={canvasRef}
              width={CANVAS_WIDTH}
              height={CANVAS_HEIGHT}
              aria-label="Draw provider signature"
              style={{
                width: '100%',
                maxWidth: CANVAS_WIDTH,
                height: 'auto',
                border: '1px solid var(--mantine-color-gray-4)',
                borderRadius: 4,
                touchAction: 'none',
                cursor: 'crosshair',
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={() => setDrawing(false)}
              onPointerCancel={() => setDrawing(false)}
            />
            <Button variant="subtle" size="xs" leftSection={<IconEraser size={14} />} onClick={clearCanvas}>
              Clear drawing
            </Button>
          </Box>
        )}
        <Group justify="flex-end">
          <Button onClick={handleSave} loading={saving} disabled={mode === 'upload' && !file}>
            Save signature
          </Button>
        </Group>
      </Stack>
    </FormCard>
  );
}
